import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ColorHash from 'color-hash'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const backgroundHash = new ColorHash({
  saturation: 0.4,
  lightness: 0.6,
})

const bidderKeyForBid = bid => {
  // Known user
  if (bid.bidder_number) {
    return bid.bidder_number
  } else {
    return `nonreg-${bid.bidder_external_identifier}`
  }
}

const hashBid = bid => {
  return backgroundHash.hex(bidderKeyForBid(bid))
}

const calculateBidderPositionalData = operations => {
  const bidsByBidder = new Map()
  const relativeIndices = new Map()

  const seenIndices = new Set()
  let relativeIndex = 0

  operations
    .slice()
    .reverse()
    .forEach(o => {
      if (o.operation !== 'BID_SUBMITTED') return

      const bid = o.data
      const bidderKey = bidderKeyForBid(bid)

      if (seenIndices.has(bidderKey)) return

      seenIndices.add(bidderKey)
      relativeIndices.set(bidderKey, relativeIndex)

      relativeIndex++
    })

  // TODO: Sort by bid timestamp here to make sure order is correct
  operations.forEach((o, absIndex) => {
    if (o.operation !== 'BID_SUBMITTED') return

    const bid = o.data
    const bidderKey = bidderKeyForBid(bid)

    if (!bidsByBidder.get(bidderKey)) {
      bidsByBidder.set(bidderKey, [])
    }

    bidsByBidder.get(bidderKey).push([absIndex, bid])
  })

  return {
    bidsByBidder,
    relativeIndices,
  }
}

const groupOperations = operations => {
  const grouped = []

  for (let i = 0; i < operations.length; i++) {
    const { operation, data } = operations[i]

    if (
      operation === 'END_STATUS_MODIFIED' &&
      ['SOLD', 'NO_SALE', 'ABORTED'].includes(data.bid_end_status)
    ) {
      grouped.push({
        ...operations[i],
        operation: 'AUCTION_SEGMENT_ENDED',
      })

      continue
    }

    // Since we know these are linear, we can advance the iterator until we no longer see end status modification
    // TODO: Aborted should be handled in a special way like the above terminal case
    if (operation === 'END_STATUS_MODIFIED') {
      if (data.bid_end_status === 'NONE') continue

      const recordedStatuses = [data]

      while (operations[i + 1]?.operation === 'END_STATUS_MODIFIED') {
        i++

        recordedStatuses.push(operations[i].data)
      }

      grouped.push({
        operation: 'END_STATUS_GROUP',
        data: {
          statuses: recordedStatuses,
        },
      })

      continue
    }

    grouped.push(operations[i])
  }

  return grouped
}

const EndStatusGroup = ({ statuses }) => {
  const seenSet = new Set(statuses.map(s => s.bid_end_status))

  return (
    <div className={cx('end-statuses')}>
      <span className={cx({ reached: seenSet.has('GOING_ONCE') })}>ONCE</span>
      <span className={cx({ reached: seenSet.has('GOING_TWICE') })}>TWICE</span>
      <span className={cx({ reached: seenSet.has('FINAL_ASK') })}>
        FINAL_ASK
      </span>
      <span className={cx({ reached: seenSet.has('FINALIZING_BID') })}>
        FINALIZING_BID
      </span>
    </div>
  )
}

EndStatusGroup.propTypes = {
  statuses: PropTypes.array,
}

// TODO: This is prefixed with Trustee only because it was built primarily with that use case in mind. The operation-based modelling would
// work just as well for REO bidding though, with some extensions for representing system bid types and so on.
const TrusteeAuctionSegmentRailroad = ({ operations }) => {
  const [selectedBidder, setSelectedBidder] = useState(null)

  const groupedOperations = groupOperations(operations)
  const entries = []

  for (let i = 0; i < groupedOperations.length; i++) {
    const { operation, data } = groupedOperations[i]

    // This is a terminal operation. It needs more work once we get some business feedback over what the summary view
    // of a segment should read.
    if (operation === 'AUCTION_SEGMENT_ENDED') {
      entries.push(
        <li key={`segment-ended-op-${i}`} className={cx('bidder-row')}>
          <h3>{data.bid_end_status}</h3>
        </li>
      )
    }

    if (operation === 'END_STATUS_GROUP') {
      entries.push(
        <li key={`end-op-${i}`} className={cx('bidder-row')}>
          <EndStatusGroup statuses={data.statuses} />
        </li>
      )
    }

    if (operation === 'BID_SUBMITTED') {
      entries.push(
        <li
          key={data.bid_id}
          className={cx({
            'bidder-row': true,
            'is-selected': selectedBidder === bidderKeyForBid(data),
          })}
          onMouseEnter={() => setSelectedBidder(bidderKeyForBid(data))}
          onMouseLeave={() => setSelectedBidder(null)}
        >
          <div>
            <h3 className={cx('bidder-name')}>
              {data.bidder_user?.name ||
                data.bidder_number ||
                `Anonymous-${data.bidder_external_identifier}`}
            </h3>
          </div>
          <div>
            <div className={cx('currency')}>{data.bid_amount}</div>
          </div>
        </li>
      )
    }
  }

  const { bidsByBidder, relativeIndices } =
    calculateBidderPositionalData(groupedOperations)

  const uniqueBidders = bidsByBidder.size
  const rowSpacing = 60
  const colSpacing = 10
  const width = (uniqueBidders + 3) * colSpacing
  const height = groupedOperations.length * rowSpacing
  const pointDiameter = 5
  const startY = (rowSpacing - pointDiameter) / 2
  const pointX = width - pointDiameter

  const railroadGroups = []

  // Per the spec, this guarantees order
  for (const [, bids] of bidsByBidder) {
    const svgDefs = []

    let firstBid = null
    let lastBid = null
    let color = null

    // Static per row markers
    svgDefs.push(
      ...bids.map(([absIndex, bid], relativeIndex) => {
        const bidderKey = bidderKeyForBid(bid)

        const cy = startY + absIndex * rowSpacing
        const offsetX =
          pointDiameter +
          colSpacing +
          relativeIndices.get(bidderKey) * colSpacing

        if (relativeIndex === 0) {
          firstBid = [absIndex, bid]

          color =
            selectedBidder && bidderKey !== selectedBidder
              ? '#eaeaea'
              : hashBid(bid)
        } else {
          lastBid = [absIndex, bid]
        }

        return (
          <>
            <circle fill={color} cx={pointX} cy={cy} r='2.5' />
            <path stroke={color} d={`M${pointX} ${cy} l-${offsetX} 0`} />
          </>
        )
      })
    )

    // NOTE: Instead of joining all intermediate points for a single bidder, we can observe that if we draw between the
    // first and last that should intersect all bids placed by the same bidder since the column is unique
    if (firstBid && lastBid) {
      const bidderKey = bidderKeyForBid(firstBid[1])

      const offsetX =
        pointDiameter + colSpacing + relativeIndices.get(bidderKey) * colSpacing

      const cx = width - offsetX - pointDiameter

      const beginY = startY + firstBid[0] * rowSpacing
      const endY = startY + lastBid[0] * rowSpacing

      svgDefs.push(
        <path stroke={color} d={`M${cx} ${beginY} L${cx} ${endY}`} />
      )
    }

    railroadGroups.push(svgDefs)
  }

  return (
    <section className={cx('bidder-railroad')}>
      <svg
        width={width}
        height={height}
        className={cx('railroad-svg')}
        viewBox={`0 0 ${width} ${height}`}
        xmlns='http://www.w3.org/2000/svg'
      >
        {railroadGroups.map((defs, i) => (
          <g key={i}>{defs}</g>
        ))}
      </svg>
      <ul
        className={cx('bidder-list', {
          'has-selection': selectedBidder !== null,
        })}
      >
        {entries}
      </ul>
    </section>
  )
}

TrusteeAuctionSegmentRailroad.propTypes = {
  operations: PropTypes.arrayOf(
    PropTypes.shape({
      operations: PropTypes.string.isRequired,
      data: PropTypes.any,
    })
  ).isRequired,
}

export default TrusteeAuctionSegmentRailroad
